import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Account } from '../types';

export interface SellDraft {
  step: number;
  price: number | '';
  currency: 'USD' | 'RUB' | 'UZS';
  gameId: string;
  serverId: string;
  server: string;
  rank: string;
  heroesCount: number | '';
  collectorTier: string;
  collectorTierLevel: number | '';
  totalSkins: number | '';
  description: string;
  skinKeys: string[];
  recallStunning: number;
  recallLuxurious: number;
  towerSkins: number;
  loadingStunning: number;
  loadingLuxurious: number;
  loadingStellar: number;
  diamonds: number;
  magicWheelProgress: number;
  confirmAccuracy: boolean;
}

const emptyDraft: SellDraft = {
  step: 1,
  price: '',
  currency: 'USD',
  gameId: '',
  serverId: '',
  server: '',
  rank: '',
  heroesCount: '',
  collectorTier: '',
  collectorTierLevel: '',
  totalSkins: '',
  description: '',
  skinKeys: [],
  recallStunning: 0,
  recallLuxurious: 0,
  towerSkins: 0,
  loadingStunning: 0,
  loadingLuxurious: 0,
  loadingStellar: 0,
  diamonds: 0,
  magicWheelProgress: 0,
  confirmAccuracy: false,
};

interface SellWizardState {
  draft: SellDraft;
  files: File[];
  editingId: string | null;
  setField: <K extends keyof SellDraft>(key: K, value: SellDraft[K]) => void;
  toggleSkin: (key: string) => void;
  setFiles: (files: File[]) => void;
  removeFile: (index: number) => void;
  addFiles: (files: File[]) => void;
  goToStep: (step: number) => void;
  loadFromAccount: (account: Account) => void;
  reset: () => void;
}

export const useSellWizardStore = create<SellWizardState>()(
  persist(
    (set) => ({
      draft: emptyDraft,
      files: [],
      editingId: null,
      setField: (key, value) => set((s) => ({ draft: { ...s.draft, [key]: value } })),
      toggleSkin: (key) =>
        set((s) => ({
          draft: {
            ...s.draft,
            skinKeys: s.draft.skinKeys.includes(key)
              ? s.draft.skinKeys.filter((k) => k !== key)
              : [...s.draft.skinKeys, key],
          },
        })),
      setFiles: (files) => set({ files }),
      addFiles: (files) => set((s) => ({ files: [...s.files, ...files].slice(0, 8) })),
      removeFile: (index) => set((s) => ({ files: s.files.filter((_, i) => i !== index) })),
      goToStep: (step) => set((s) => ({ draft: { ...s.draft, step } })),
      loadFromAccount: (account) =>
        set({
          editingId: account.id,
          files: [],
          draft: {
            step: 1,
            price: account.price,
            currency: account.currency as SellDraft['currency'],
            gameId: account.gameId ?? '',
            serverId: account.serverId ?? '',
            server: account.server,
            rank: account.rank,
            heroesCount: account.heroesCount ?? '',
            collectorTier: account.collectorTier ?? '',
            collectorTierLevel: account.collectorTierLevel ?? '',
            totalSkins: account.totalSkins,
            description: account.description ?? '',
            skinKeys: account.skins.map((s) => s.key),
            recallStunning: account.recallStunning,
            recallLuxurious: account.recallLuxurious,
            towerSkins: account.towerSkins,
            loadingStunning: account.loadingStunning,
            loadingLuxurious: account.loadingLuxurious,
            loadingStellar: account.loadingStellar,
            diamonds: account.diamonds,
            magicWheelProgress: account.magicWheelProgress,
            confirmAccuracy: false,
          },
        }),
      reset: () => set({ draft: emptyDraft, files: [], editingId: null }),
    }),
    {
      name: 'mlbb-sell-draft',
      partialize: (state) => ({ draft: state.draft, editingId: state.editingId }), // File objects aren't serializable — screenshots aren't persisted
    }
  )
);
